import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { blockUserAction, usersDetails } from "../actions/user";
import ContentLoader from "react-content-loader";
import { backendImageUrl } from "../Router/constant";
import Modal from "react-bootstrap/Modal";

export default function Users() {
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const { userList, userListLoader } = useSelector((state) => state.user);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;
  const [show, setShow] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(delay);
  }, [search]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [userList]);

  useEffect(() => {
    dispatch(usersDetails(debouncedSearch, page, limit));
  }, [debouncedSearch, page]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("sv-SE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDelete = (user) => {
    setShow(true);
    setSelectedUser(user);
  };

  const handleClose = () => setShow(false);

  const blockUser = async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      await dispatch(blockUserAction(selectedUser._id, selectedUser.isBlocked));
      setShow(false);
      dispatch(usersDetails(search, page, limit));
    } finally {
      setLoading(false);
    }
  };

  const totalPages = userList?.totalPages || 1;
  const handleSearch = (e) => {
    setPage(1);
    setSearch(e.target.value);
  };

  const getPagination = (page, totalPages) => {
    const pages = [];

    // Always show first page
    pages.push(1);

    // Left dots
    if (page > 2) {
      pages.push("...");
    }

    // Current & next page
    if (page > 1 && page < totalPages) {
      pages.push(page);
    }

    if (page + 1 < totalPages) {
      pages.push(page + 1);
    }

    // Right dots
    if (page + 2 < totalPages) {
      pages.push("...");
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return [...new Set(pages)];
  };

  return (
    <>
      {userListLoader ? (
        <div className="container-fluid contactSkeleton">
          {[...Array(6)].map((_, i) => (
            <ContentLoader key={i} width="100%" height={50}>
              <rect x="0" y="0" rx="5" ry="5" width="100%" height="50" />
            </ContentLoader>
          ))}
        </div>
      ) : (
        <div className="container-fluid mainConatinerSection">
          <div className="card card-flush">
            <div className="card-header align-items-center py-5 gap-2 gap-md-5">
              <div className="card-title">
                <div className="d-flex align-items-center position-relative my-1">
                  <i className="ki-duotone ki-magnifier fs-3 position-absolute ms-4">
                    <span className="path1 searchPath"></span>
                    <span className="path2 searchPath"></span>
                  </i>
                  <input
                    ref={inputRef}
                    type="text"
                    className="form-control form-control-solid w-250px ps-12"
                    placeholder="Search user"
                    value={search}
                    onChange={handleSearch}
                  />
                </div>
              </div>
            </div>
            <div className="card-body pt-0 overFlowScroll">
              <table className="table align-middle table-row-dashed fs-6 gy-5">
                <thead>
                  <tr className="text-gray-500 fw-bold fs-7 text-uppercase">
                    <th>User Name</th>
                    <th className="text-center">Email</th>
                    <th className="text-center">Phone</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Created Date</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>

                <tbody className="fw-semibold text-gray-600">
                  {userList?.users?.length > 0 ? (
                    userList?.users?.map((item, index) => (
                      <tr key={item._id || index}>
                        <td>
                          <span
                            className="symbol-label me-3"
                            style={{
                              backgroundImage: `url(${backendImageUrl}${item.photo})`,
                            }}
                          />
                          {item.name || "Admin"}
                        </td>

                        <td className="text-center">{item.email}</td>
                        <td className="text-center">{item.phone}</td>

                        <td className="text-center">
                          <span
                            className={`badge ${item.isBlocked
                              ? "badge-light-danger"
                              : "badge-light-success"
                              }`}
                          >
                            {item.isBlocked ? "Blocked" : "Active"}
                          </span>
                        </td>

                        <td className="text-center">
                          {formatDate(item.createdAt)}
                        </td>

                        <td className="text-center">
                          <button
                            className={`btn btn-sm badge ${item.isBlocked
                              ? "custom-hover-success"
                              : "custom-hover-danger"
                              }`}
                            onClick={() => handleDelete(item)}                          >
                            {item.isBlocked ? "UnBlock" : "Block"}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-5 text-muted">
                        No user found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {userList?.users?.length > 0 && (
                <div className="row mt-4">
                  <div className="col-md-5">
                    <strong>Total: {userList?.total}</strong>
                  </div>

                  <div className="col-md-7 d-flex justify-content-end">
                    <ul className="pagination">
                      {/* Prev */}
                      {page > 1 && (
                        <li className="page-item">
                          <button
                            className="page-link"
                            onClick={() => setPage(page - 1)}
                          >
                            Prev
                          </button>
                        </li>
                      )}

                      {getPagination(page, totalPages).map((item, index) => (
                        <li
                          key={index}
                          className={`page-item ${item === page ? "activePagination" : ""
                            } ${item === "..." ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link"
                            disabled={item === "..."}
                            onClick={() => item !== "..." && setPage(item)}
                          >
                            {item}
                          </button>
                        </li>
                      ))}

                      {/* Next */}
                      {page < totalPages && (
                        <li className="page-item">
                          <button
                            className="page-link"
                            onClick={() => setPage(page + 1)}
                          >
                            Next
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
              {/* End Pagination */}
            </div>
          </div>
        </div>
      )}

      <Modal show={show} onHide={handleClose}>
        <div className="LogoutSection">
          <h6 className="m-0">
            {selectedUser?.isBlocked ? "Unblock User" : "Block User"}
          </h6>

          <p className="m-0">
            Are you sure you want to{" "}
            {selectedUser?.isBlocked ? "unblock" : "block"} this user?
          </p>

          <div className="logoutbuttonSection">
            <button
              className="redButton fontBthSize"
              onClick={handleClose}
              disabled={loading}
            >
              Close
            </button>

            <button
              className={`fontBthSize ${selectedUser?.isBlocked ? "greenButton" : "redButton"
                }`}
              onClick={blockUser}
              disabled={loading}

            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Processing...
                </>
              ) : selectedUser?.isBlocked ? (
                "Unblock"
              ) : (
                "Block"
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
